/**
 * Sub-component (not a routed page): ground-truth editor for search training data.
 * Renders the list of extracted search results, expanding the active one into a
 * SearchSingleResultForm and collapsing the rest to image/title rows, with add and
 * delete controls. State lives in the parent (TrainingDataCreateDisplay) and is
 * mutated through the use-immer Updater prop; no API calls are made here.
 */
import React, { useState } from "react";
import { SearchResultsExtractedData, SearchSingleResultExtractedData } from "@/types/trainingData";
import { Updater } from "use-immer";
import SearchSingleResultForm from "@/pages/admin/ai/Search/SearchSingleResultForm";
import { Trash2 } from "lucide-react";

interface SearchExtractedDataFormProps {
    searchResultsExtractedData: SearchResultsExtractedData | null;
    setSearchResultsExtractedData: Updater<SearchResultsExtractedData | null>;
    readonly?: boolean;
}

const defaultSingleResult = (): SearchSingleResultExtractedData => ({
    title: "",
    description: "",
    size: "",
    price: "0.00",
    imageUrl: "",
    productUrl: "",
});

const isResultComplete = (result: SearchSingleResultExtractedData): boolean => {
    return (
        result.title.trim() !== "" &&
        (result.description === undefined ||
            result.description === null ||
            result.description.trim() !== "") &&
        result.price !== "" &&
        result.price !== undefined &&
        parseFloat(result.price) >= 0 &&
        result.imageUrl.trim() !== "" &&
        result.productUrl.trim() !== ""
    );
};

const SearchExtractedDataForm: React.FC<SearchExtractedDataFormProps> = ({
    searchResultsExtractedData,
    setSearchResultsExtractedData,
    readonly = false,
}) => {
    const [activeIndex, setActiveIndex] = useState(0);

    if (!searchResultsExtractedData) return null;

    const handleAddResult = () => {
        // If there are existing results, validate the current one before adding a new one
        if (searchResultsExtractedData.results.length > 0) {
            const currentResult = searchResultsExtractedData.results[activeIndex];
            if (!isResultComplete(currentResult)) {
                alert("Please fill out all fields in the current result before adding a new one.");
                return;
            }
        }

        const newIndex = searchResultsExtractedData.results.length;
        setSearchResultsExtractedData((draft) => {
            if (draft) {
                draft.results.push(defaultSingleResult());
            }
        });
        setActiveIndex(newIndex);
    };

    const handleDeleteResult = (index: number) => {
        setSearchResultsExtractedData((draft) => {
            if (draft) {
                draft.results.splice(index, 1);
            }
        });
        setActiveIndex((prev) => {
            if (prev > index) return prev - 1;
            if (prev === index) return Math.max(0, prev - 1);
            return prev;
        });
    };

    const results = searchResultsExtractedData.results;
    const canAddNewResult =
        results.length === 0 ||
        (activeIndex < results.length && isResultComplete(results[activeIndex]));

    return (
        <div className="flex flex-col h-full min-h-0">
            <h3 className="text-sm font-medium mb-2 tracking-widest flex-none">Extracted Data</h3>
            {/* Results section */}
            <div className="flex flex-col flex-1 min-h-0">
                <h4 className="text-sm font-medium tracking-widest mb-4 flex-none">Results</h4>
                <div className="overflow-y-auto pr-2 space-y-4 flex-1">
                    {results.map((result, index) => {
                        const isActive = index === activeIndex;
                        return (
                            <div key={index} className="border rounded-lg p-2 bg-white shadow-sm">
                                {isActive ? (
                                    <SearchSingleResultForm
                                        index={index}
                                        result={result}
                                        setSearchResultsExtractedData={
                                            setSearchResultsExtractedData
                                        }
                                        onDelete={() => handleDeleteResult(index)}
                                        readonly={readonly}
                                    />
                                ) : (
                                    <div
                                        className="flex items-center justify-between cursor-pointer"
                                        onClick={() => setActiveIndex(index)}
                                    >
                                        <div className="flex items-center space-x-4">
                                            {result.imageUrl && (
                                                <img
                                                    src={result.imageUrl}
                                                    alt={result.title}
                                                    className="w-16 h-16 object-cover rounded"
                                                />
                                            )}
                                            <span className="text-sm font-medium truncate">
                                                {result.title || `Result ${index + 1}`}
                                            </span>
                                        </div>
                                        {!readonly && (
                                            <button
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    handleDeleteResult(index);
                                                }}
                                                className="text-red-600 hover:text-red-800"
                                            >
                                                <Trash2 className="w-5 h-5" />
                                            </button>
                                        )}
                                    </div>
                                )}
                            </div>
                        );
                    })}
                    {!readonly && (
                        <button
                            onClick={handleAddResult}
                            disabled={!canAddNewResult}
                            className={`mt-2 px-3 py-1 text-sm rounded-lg ${
                                canAddNewResult
                                    ? "bg-gray-200 hover:bg-gray-300"
                                    : "bg-gray-100 text-gray-400 cursor-not-allowed"
                            }`}
                        >
                            + add result
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
};

export default SearchExtractedDataForm;
